import { useMemo, useSyncExternalStore } from 'react';
import { fetchQuery, graphql, loadQuery, useRelayEnvironment } from 'react-relay';
import type { PreloadedQuery } from 'react-relay';
import type { GraphQLTaggedNode } from 'relay-runtime';

import type { usePerformanceInsightsAccessQuery } from './__generated__/usePerformanceInsightsAccessQuery.graphql.ts';

export const performanceInsightsAccessQuery: GraphQLTaggedNode = graphql`
	query usePerformanceInsightsAccessQuery {
		entitlements {
			performanceInsights {
				isEntitled
			}
		}
	}
`;

type AccessSnapshot = {
	queryRef: PreloadedQuery<usePerformanceInsightsAccessQuery> | null;
	error: Error | undefined;
	resultVersion: number;
	retryVersion: number;
};
type RelayEnvironment = ReturnType<typeof useRelayEnvironment>;
type AccessController = {
	getSnapshot: () => AccessSnapshot;
	subscribe: (listener: () => void) => () => void;
	retry: () => void;
	dispose: () => void;
};

let controllers = new WeakMap<RelayEnvironment, AccessController>();
const activeControllers = new Set<AccessController>();

const createController = (environment: RelayEnvironment): AccessController => {
	// Share request health and reference ownership; entitlement data stays in Relay.
	let snapshot: AccessSnapshot = {
		queryRef: null,
		error: undefined,
		resultVersion: 0,
		retryVersion: 0,
	};
	const listeners = new Set<() => void>();
	let cancelRequest: (() => void) | undefined;
	let interval: number | undefined;
	let inFlight = false;
	let generation = 0;
	const publish = (patch: Partial<AccessSnapshot>) => {
		snapshot = { ...snapshot, ...patch };
		listeners.forEach((listener) => listener());
	};
	const fail = (error: Error) => publish({ error, resultVersion: snapshot.resultVersion + 1 });
	const refresh = () => {
		if (inFlight) return;
		inFlight = true;
		const requestGeneration = generation;
		let receivedResponse = false;
		const request = fetchQuery<usePerformanceInsightsAccessQuery>(
			environment,
			performanceInsightsAccessQuery,
			{},
			{ fetchPolicy: 'network-only' },
		).subscribe({
			next: (data) => {
				if (requestGeneration !== generation) return;
				receivedResponse = true;
				if (typeof data.entitlements?.performanceInsights?.isEntitled !== 'boolean') {
					fail(new Error('Performance Insights entitlement metadata is unavailable'));
					return;
				}
				const queryRef =
					snapshot.queryRef ??
					loadQuery<usePerformanceInsightsAccessQuery>(
						environment,
						performanceInsightsAccessQuery,
						{},
						{ fetchPolicy: 'store-only' },
					);
				publish({ queryRef, error: undefined, resultVersion: snapshot.resultVersion + 1 });
			},
			error: (error: Error) => {
				if (requestGeneration !== generation) return;
				inFlight = false;
				fail(error);
			},
			complete: () => {
				if (requestGeneration !== generation) return;
				inFlight = false;
				if (!receivedResponse)
					fail(new Error('Performance Insights entitlement lookup returned no result'));
			},
		});
		cancelRequest = () => request.unsubscribe();
	};
	const refreshWhenVisible = () => {
		if (window.document?.visibilityState === 'visible') refresh();
	};
	const stop = () => {
		generation += 1;
		cancelRequest?.();
		cancelRequest = undefined;
		inFlight = false;
		window.clearInterval(interval);
		window.removeEventListener('focus', refreshWhenVisible);
		window.document?.removeEventListener('visibilitychange', refreshWhenVisible);
		snapshot.queryRef?.dispose();
		snapshot = { ...snapshot, queryRef: null, error: undefined };
		activeControllers.delete(controller);
	};
	const controller: AccessController = {
		getSnapshot: () => snapshot,
		subscribe: (listener) => {
			const firstSubscriber = listeners.size === 0;
			listeners.add(listener);
			if (firstSubscriber) {
				activeControllers.add(controller);
				interval = window.setInterval(refreshWhenVisible, 60_000);
				window.addEventListener('focus', refreshWhenVisible);
				window.document?.addEventListener('visibilitychange', refreshWhenVisible);
			}
			refresh();
			return () => {
				listeners.delete(listener);
				if (listeners.size === 0 && activeControllers.has(controller)) stop();
			};
		},
		retry: () => {
			publish({ retryVersion: snapshot.retryVersion + 1 });
			refresh();
		},
		dispose: () => {
			listeners.clear();
			stop();
		},
	};
	return controller;
};

const getController = (environment: RelayEnvironment): AccessController => {
	let controller = controllers.get(environment);
	if (!controller) {
		controller = createController(environment);
		controllers.set(environment, controller);
	}
	return controller;
};

type PerformanceInsightsAccessState = AccessSnapshot & {
	isInitialLoading: boolean;
	retry: () => void;
};

export const usePerformanceInsightsAccess = (): PerformanceInsightsAccessState => {
	const environment = useRelayEnvironment();
	const controller = useMemo(() => getController(environment), [environment]);
	// New route entry waits for a fresh result even when a mounted sidebar has verified access.
	const entryVersion = useMemo(() => controller.getSnapshot().resultVersion, [controller]);
	const snapshot = useSyncExternalStore(
		controller.subscribe,
		controller.getSnapshot,
		controller.getSnapshot,
	);
	return {
		...snapshot,
		isInitialLoading: snapshot.resultVersion <= entryVersion,
		retry: controller.retry,
	};
};

export const clearPerformanceInsightsAccessForTesting = (): void => {
	activeControllers.forEach((controller) => controller.dispose());
	controllers = new WeakMap();
};
